import { useDomainFromUrl } from '@/app/_common/domain/use-domain-from-url'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import type { Message, Moderation, Pagination } from '@/hooks/api/_types'
import { useApiRequest } from '@/lib/http-utils'
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { TagsStructure } from './use-table-details-query'

type TurnStatusAPIResponse = {
  data: Turn
  pagination: Pagination
  streaming: boolean
}

type ErrorObject = {
  errorCode: string
  status: number
}

export type EnrichmentTableColumn = {
  columnName: string
  columnDescription?: string
  dataType: string
  distinctCount?: number
  nullCount?: number
  minValue?: string | number | null
  maxValue?: string | number | null
  sampleValues?: (string | number)[]
  categories?: string[]
  businessDefinition?: string
  businessRules?: string
  tags?: TagsStructure
  previousTags?: TagsStructure
  previousBusinessDefinition?: string
  previousBusinessRules?: string
  previousColumnDescription?: string
}

export type EnrichmentTable = {
  tableName: string
  tableDomain?: string
  tableDescription?: string
  tableOwners?: string[]
  tableVersion?: string
  rowCount?: number
  columnCount?: number
  allColumns?: string[]
  columns?: EnrichmentTableColumn[]
  tags?: TagsStructure
}

export type EnrichmentOutputData = {
  serviceName: string
  databaseName: string
  databaseSource?: string
  schemaName: string
  schemaDescription?: string
  schemaOwners?: string[]
  tables: EnrichmentTable[]
  operation_status?: { message?: string }
}

type TurnStepStatus = 'SUCCESS' | 'FAILURE' | 'IN_PROGRESS' | 'CANCELLED'
type TurnStatus = TurnStepStatus | 'QUEUED' | 'PLANNING' | 'CANCELLED'
export type TurnInputAttachment = {
  fileId: string
  fileName: string
  purpose?: string
}
export type StructuredOutput = {
  type: string
  data: EnrichmentOutputData
}

type TurnOutput = string | Message['messageContent']['content'] | StructuredOutput[]

type Turn = {
  turnId: string
  moderation: Moderation
  status: TurnStatus
  input: { content: string; attachments?: TurnInputAttachment[] }
  startTimeEpoch: number
  endTimeEpoch: number
  output: TurnOutput
  error: ErrorObject | null
  steps: TurnStep[] | null
  assistantMessageId: string | null
}

type TurnStepOutput = {
  response:
    | string
    | {
        statueCode: number
        message: string
        data: unknown
        error: boolean
      }
  taskDescription: string
  toolName?: string
  instruction?: string
}

type TurnStep = {
  stepId: string
  startEpoch: number
  endEpoch: number
  description?: string
  status: TurnStepStatus
  output: Array<string | TurnStepOutput> | null
  error: ErrorObject | null
  tasks: TurnStepTask[] | null
}

type TurnStepTask = {
  taskId: string
  agentId: string
  agentVersion: string
  agentInstruction: string
  startEpoch: number
  endEpoch: number
  taskDescription: string
  output: TurnStepOutput | TurnStepOutput[] | null
  error: ErrorObject | null
  status: 'SUCCESS' | 'FAILURE' | 'IN_PROGRESS' | 'QUEUED'
  updates: TurnStepTaskUpdate[] | null
}

type LiveSessionUpdateContent = {
  content: string
  liveSessionView: string | null
  recordingUrl: string | null
  journeyViewReference: string | null
  uploadFileReference: string | null
  messageType:
    | 'pause'
    | 'resume'
    | 'info'
    | 'live_view_url'
    | 'request_user_input'
    | 'recording'
    | 'upload_file_reference'
    | null
}

type TurnStepTaskUpdate = {
  updateTimestamp: number
  output: {
    content: string | LiveSessionUpdateContent
  }
}

const useFetchTurnStatus = () => {
  const { apiRequest, domainId } = useApiRequest()
  return async ({ workflowId, turnId }: { workflowId: string; turnId: string }) => {
    if (!turnId) throw new Error('FETCH_TURN_STATUS_CALLED_WITH_TURN_ID_NEW')

    const response = await apiRequest({
      path: taskManagerApiPaths.turnStatus({ workflowId, turnId, domainId }),
    })

    if (!response.ok) throw new Error('FAILED_TO_GET_TURN_STATUS')

    const data = (await response.json()) as TurnStatusAPIResponse

    if (!data?.data) throw new Error('INVALID_TURN_STATUS_RESPONSE')

    return data.data
  }
}

function useTurnStatusQuery({
  workflowId,
  turnId,
  queryOptions = {},
}: {
  workflowId: string
  turnId: string
  queryOptions?: Omit<UseQueryOptions<Turn>, 'queryKey' | 'queryFn'>
}) {
  const { domainId } = useDomainFromUrl()
  const fetchTurnStatus = useFetchTurnStatus()
  return useQuery({
    queryKey: turnStatusKeyFactory({ workflowId, turnId, domainId }),
    queryFn: () => fetchTurnStatus({ workflowId, turnId }),
    ...queryOptions,
  })
}
const turnStatusKeyFactory = ({
  workflowId,
  domainId,
  turnId,
}: {
  workflowId: string
  domainId: string
  turnId?: string
}) => ['turn-status', domainId, workflowId, ...(turnId ? [turnId] : [])]

export {
  turnStatusKeyFactory,
  useFetchTurnStatus,
  useTurnStatusQuery,
  type LiveSessionUpdateContent,
  type Turn,
  type TurnStatus,
  type TurnStatusAPIResponse,
  type TurnStep,
  type TurnStepOutput,
  type TurnStepStatus,
  type TurnStepTask,
  type TurnStepTaskUpdate,
}
